import { PolicyFilterState } from "elements/Filter/PolicyFilter";
import {
  GroupInfo,
  SiteInfo
} from "../../Settings/ProvisionedDeviceSettingsRouter";
import { PolicyTypes } from "../ProvisionedDesktopDeviceSettings";
import {
  DesktopDevicePolicyScope,
  HardwareModelProps,
  UserGroupInfo
} from "../ProvisionedDesktopDeviceSettingsRouter";
import { BasicDesktopDeviceSettings } from "../schemas/types";

export const booleanFromString = (
  value: boolean | string | number
): boolean | string | number => {
  if (typeof value !== "string") {
    return value;
  }
  if (["true", "false"].includes(value.toLowerCase())) {
    return "true" === value.toLowerCase();
  }

  return value;
};

export const getStringScopeRule = (
  group?: GroupInfo,
  site?: SiteInfo,
  userGroup?: UserGroupInfo,
  hardwareModel?: HardwareModelProps
) => {
  if (group?.id) {
    return `{ equal: { key: "group", value: "${group.id}" } }`;
  }
  if (site?.id) {
    return `{ equal: { key: "site", value: "${site.id}" } }`;
  }
  if (userGroup?.id) {
    return `{ equal: { key: "user_group", value: "${userGroup.id}" } }`;
  }
  return `{ equal: { key: "model", value: "${hardwareModel?.id}" } }`;
};

export const getScopeRule = (
  scope: string | undefined,
  filterState: PolicyFilterState,
  modelId: string | undefined
) => {
  if (scope === PolicyTypes.GROUP || scope === DesktopDevicePolicyScope.Group) {
    return [{ equal: { key: "group", value: filterState.group } }];
  }
  if (scope === PolicyTypes.SITE || scope === DesktopDevicePolicyScope.Site) {
    return [{ equal: { key: "site", value: filterState.site } }];
  }
  if (scope === PolicyTypes.MODEL || scope === DesktopDevicePolicyScope.Model) {
    return [{ equal: { key: "model", value: modelId } }];
  }
  if (
    scope === PolicyTypes.USER_GROUP ||
    scope === DesktopDevicePolicyScope.User_Group
  ) {
    return [{ equal: { key: "user_group", value: filterState.user_group } }];
  }
  if (
    scope === PolicyTypes.DEVICE ||
    scope === DesktopDevicePolicyScope.Device
  ) {
    return [{ equal: { key: "device", value: filterState.device } }];
  }
  return [];
};

export const getPriority = (
  scope: string | undefined,
  currentPriority?: string
) => {
  if (
    scope === PolicyTypes.DEVICE ||
    scope === DesktopDevicePolicyScope.Device
  ) {
    return "0.0";
  }
  if (
    scope === PolicyTypes.USER_GROUP ||
    scope === DesktopDevicePolicyScope.User_Group
  ) {
    return currentPriority || "0.3";
  }
  if (scope === PolicyTypes.SITE || scope === DesktopDevicePolicyScope.Site) {
    return "1";
  }
  if (scope === PolicyTypes.GROUP || scope === DesktopDevicePolicyScope.Group) {
    return "2";
  }
  if (scope === PolicyTypes.MODEL || scope === DesktopDevicePolicyScope.Model) {
    return "3";
  }
  return "4";
};

export function generateBlankDesktopDeviceSettings(): BasicDesktopDeviceSettings {
  return {
    com: {
      poly: {
        audio: {
          tone_control: {
            value: undefined
          },
          acoustic_fence: {
            value: undefined
          },
          anc_timeout: {
            value: undefined
          },
          auto_pause_music: {
            value: undefined
          },
          audio_quality: {
            value: undefined
          },
          streaming_audio: {
            value: undefined
          },
          extended_range: {
            value: undefined
          },
          volume_level_indicators: {
            value: undefined
          },
          independent_volume_control: {
            value: undefined
          },
          volume_alerts: {
            value: undefined
          },
          noise_exposure_limit: {
            value: undefined
          },
          hours_on_phone_per_day: {
            value: undefined
          },
          anti_startle: {
            value: undefined
          },
          g616: {
            value: undefined
          },
          noise_block_ai: {
            value: undefined
          },
          stereo: {
            value: undefined
          },
          bass: {
            value: undefined
          },
          treble: {
            value: undefined
          }
        },
        video: {
          brightness: {
            value: undefined
          },
          contrast: {
            value: undefined
          },
          hue: {
            value: undefined
          },
          saturation: {
            value: undefined
          },
          sharpness: {
            value: undefined
          },
          gamma: {
            value: undefined
          },
          white_balance: {
            value: undefined
          },
          white_balance_auto_enabled: {
            value: undefined
          },
          back_light_compensation: {
            value: undefined
          },
          gain: {
            value: undefined
          },
          zoom: {
            value: undefined
          },
          exposure: {
            value: undefined
          },
          exposure_auto_enabled: {
            value: undefined
          },
          focus: {
            value: undefined
          },
          focus_auto_enabled: {
            value: undefined
          },
          skin_enhancement: {
            value: undefined
          },
          wide_dynamic_range: {
            value: undefined
          },
          participant_count: {
            value: undefined
          },
          onscreen_display: {
            value: undefined
          },
          tracking_speed: {
            value: undefined
          },
          tracking_mode: {
            value: undefined
          },
          anti_flicker: {
            value: undefined
          },
          frame_size: {
            value: undefined
          },
          max_zoom: {
            value: undefined
          },
          camera_movement: {
            value: undefined
          }
        },
        call_control: {
          caller_id: {
            value: undefined
          },
          answering_call_alert: {
            value: undefined
          },
          mute_off_alert: {
            value: undefined
          },
          auto_answer_cradle: {
            value: undefined
          },
          default_phone_line: {
            value: undefined
          },
          audio_sensing: {
            value: undefined
          },
          answer_voice_prompt: {
            value: undefined
          },
          mute_reminder_timing: {
            value: undefined
          },
          mute_alert_type: {
            value: undefined
          },
          audio_channel_tone: {
            value: undefined
          },
          mute_reminder_mode: {
            value: undefined
          },
          mute_alerts: {
            value: undefined
          },
          close_conversation_limiting: {
            value: undefined
          },
          notification_tones: {
            value: undefined
          },
          online_indicator: {
            value: undefined
          },
          rocket_button: {
            value: undefined
          },
          call_announcement: {
            value: undefined
          },
          auto_answer: {
            value: undefined
          },
          smart_audio_transfer: {
            value: undefined
          },
          call_button_lock: {
            value: undefined
          },
          auto_mute: {
            value: undefined
          },
          active_call_audio: {
            value: undefined
          },
          auto_connect_mobile: {
            value: undefined
          },
          auto_disconnect_cradle: {
            value: undefined
          },
          mobile_voice_commands: {
            value: undefined
          },
          over_air_subscription: {
            value: undefined
          },
          range: {
            value: undefined
          },
          computer_audio_bandwidth: {
            value: undefined
          },
          desk_phone_audio_bandwidth: {
            value: undefined
          },
          hd_voice: {
            value: undefined
          },
          second_incoming_call: {
            value: undefined
          },
          computer_volume: {
            value: undefined
          },
          desk_phone_volume: {
            value: undefined
          },
          mobile_phone_volume: {
            value: undefined
          },
          computer_ringtone: {
            value: undefined
          },
          desk_phone_ringtone: {
            value: undefined
          },
          mobile_phone_ringtone: {
            value: undefined
          },
          default_ringtone: {
            value: undefined
          },
          mute_reminder_volume: {
            value: undefined
          },
          audio_prompt_volume: {
            value: undefined
          },
          sidetone: {
            value: undefined
          },
          wearing_preference: {
            value: undefined
          },
          ring_vibration: {
            value: undefined
          },
          increase_qd_headset_volume: {
            value: undefined
          },
          dialtone: {
            value: undefined
          }
        },
        wireless: {
          bluetooth: {
            value: undefined
          },
          exclusive_connection: {
            value: undefined
          },
          connection_indication: {
            value: undefined
          },
          bluetooth_streaming: {
            value: undefined
          },
          secure_bluetooth: {
            value: undefined
          }
        },
        general: {
          language: {
            value: undefined
          },
          battery_status_alert: {
            value: undefined
          },
          sensor_settings_enabled: {
            value: undefined
          },
          charge_vibration: {
            value: undefined
          },
          restore_defaults: {
            value: undefined
          }
        },
        software_update: {
          policy: {
            use_latest: {
              value: undefined
            },
            version: {
              value: undefined
            }
          }
        }
      }
    }
  };
}
